import React, {useState, Fragment} from 'react';
import styled from 'styled-components';
import ImageFadeIn from 'react-image-fade-in';
import {
  Body,
  Caption1,
  Title1,
  Title3,
} from '../../../../components/Typography';
import Hr from '../../../../components/Hr';
import FavouriteIconContainer from '../../FavouriteIcon/FavouriteIconContainer';
import ProductCustomizerContainer from '../../../ProductCustomizer/ProductCustomizerContainer';
import {ButtonRow, AddButton} from '../../../../components/Buttons';
import optionsForProductSummary from '../../../OrderSummary/ProductSummary/utils/optionsForProductSummary';
import renderSelectedItemsString from '../../../../utils/renderSelectedItemsString';
import calculatePrice from '../../../../utils/calculatePrice';
import {EditIcon} from '../../../../components/Icons';
import appConfig from '../../../../../app-config';
import {modifyFavouriteProducts} from '../utils/helper';

const TitleWrapper = styled.div`
  width: max-content;
  font-weight: 600;
  margin-bottom: 20px;

  :after {
    content: '';
    display: block;
    // width: calc(100% + 2px);
    padding-top: 2px;
    border-bottom: ${props => props.categoriesBarSelectedBorderColorWidth} solid
      ${props => props.theme.categoriesBarSelectedBorderColor};
  }
`;

const OrderAgainHr = styled(Hr)`
  background-color: ${props => props.theme.secondaryColor};
  height: 1px;
`;

const CardWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;

  @media (max-width: 1265px) {
    grid-template-columns: 1fr;
  }
`;

// grid-template-columns: ${props =>
//   props.theme.productPreviewAlignImage === 'left'
//     ? '145px 1fr'
//     : '1fr 140px'};

// @media (max-width: 768px) {
//   grid-template-columns: 100px 1fr;
//   grid-template-rows: 100px;
// }

const Wrapper = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: ${props =>
    props.isNoImage
      ? '1fr'
      : props.theme.productPreviewAlignImage === 'left'
      ? '150px 1fr'
      : '1fr 150px'};
  margin: 10px 10% 10px 0;
  background: white;
  overflow: hidden;
  // cursor: pointer;
  //box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.3);

  :hover {
    background-color: ${props => props.theme.menuCardHoverColor};
    transition: all 0.25s ease;
  }

  @media (max-width: 400px) {
    grid-template-columns: ${props =>
      props.isNoImage ? '1fr' : '91px 1fr !important'};
    padding: 0;
  }

  @media (max-width: 500px) {
    grid-template-columns: ${props => (props.isNoImage ? '1fr' : '140px 1fr')};
    //grid-template-rows: 148px;
  }

  @media (min-width: 501px) and (max-width: 768px) {
    grid-template-columns: ${props => (props.isNoImage ? '1fr' : '185px 1fr')};
    grid-template-rows: 185px;
  }

  @media (max-width: 400px) {
    grid-template-rows: unset;
  }
`;

const ImageWrapper = styled.div`
  order: ${props => (props.theme.productPreviewAlignImage === 'left' ? 0 : 2)};
  padding: 6px;
  @media (max-width: 400px) {
    height: 100px;
  }
`;

const Image = styled(ImageFadeIn)`
  //height: 100%;
  object-fit: cover;
  border-radius: 50%;
  border: ${props => props.theme.favouritesImageBorder};
  filter: ${props => (props.isAvailable ? null : 'grayscale(0.9)')};
  height: 138px;

  @media (max-width: 400px) {
    height: 79px;
  }

  @media (min-width: 501px) and (max-width: 768px) {
    height: 173px;
  }
`;

const InfoSection = styled.div`
  display: grid;
  padding: 14px;
  grid-gap: 10px;
`;

const NameAndHeart = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Description = styled(Caption1)`
  color: ${props =>
    props.isAvailable ? 'inherit' : props.theme.unavailableProductColor};
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${props => (props.isAvailable ? 'inherit' : props.theme.gray)};

  // @media (max-width: 768px) {
  //   display: none;
  // }
`;

const NoFavouritesFound = styled(Title1)`
  text-align: center;
  color: ${props => props.theme.unavailableProductColor};
  margin-top: 64px;
`;

const Name = styled(Title3)`
  font-weight: 600;
  color: ${props =>
    props.isAvailable ? 'inherit' : props.theme.unavailableProductColor};
`;

const OptionName = styled.span`
  font-weight: 600;
`;

const FavouriteButtonRow = styled(ButtonRow)`
  align-items: center;
  grid-gap: 8px;

  @media (max-width: 768px) {
    grid-auto-flow: column;
  }
  @media (max-width: 420px) {
    grid-auto-flow: row;
  }
`;

const CustomizeButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 8px;
  align-items: center;
  cursor: pointer;
  color: ${props => props.theme.customizeButtonColor};
  font-family: ${props => props.theme.primaryFontFontFamily};
  cursor: ${props => (props.isAvailable ? 'pointer' : 'default')};

  &:hover {
    color: ${props => props.theme.customizeButtonHoverColor};
    transition: all 0.25s ease;
  }
`;

const OptionNameSpan = styled.span`
  font-weight: bold;
`;

const FavouritesList = ({
  favouritesList,
  editPreviousProductConfiguration,
  isFromFavourites,
  addToCart,
  products,
}) => {
  return (
    <div style={{marginBottom: '35px'}}>
      <TitleWrapper>
        <Title3 id="FavouritesList-Title" style={{fontWeight: '500'}}>
          {appConfig.appSource === 'picassos' ||
          appConfig.appSource === 'hat_six'
            ? 'Favorites'
            : 'Favourites'}
        </Title3>
      </TitleWrapper>
      <CardWrapper>
        {!favouritesList.length ? (
          <NoFavouritesFound id="FavouritesList-NotFound">
            No{' '}
            {appConfig.appSource === 'picassos' ||
            appConfig.appSource === 'hat_six'
              ? 'Favorites'
              : 'Favourites'}{' '}
            Found
          </NoFavouritesFound>
        ) : (
          favouritesList.map((favourite, index) => (
            <Wrapper
              key={favourite.favouriteId}
              isNoImage={appConfig.isNoImage}
            >
              {!appConfig.isNoImage && (
                <ImageWrapper>
                  <Image
                    src={favourite.flatProduct.image_url}
                    isAvailable={favourite.flatProduct.is_product_available}
                  />
                </ImageWrapper>
              )}
              <InfoSection>
                <NameAndHeart>
                  <Name
                    isAvailable={favourite.flatProduct.is_product_available}
                  >
                    {favourite.flatProduct.name}
                  </Name>
                  <FavouriteIconContainer
                    isFavourited={true}
                    shouldAlignCenter={false}
                    favouriteId={favourite.favouriteId}
                  />
                </NameAndHeart>
                {favourite.flatProduct.user_description && (
                  <Description
                    isAvailable={favourite.flatProduct.is_product_available}
                  >
                    {optionsForProductSummary(favourite.flatProduct) &&
                      optionsForProductSummary(favourite.flatProduct)
                        .map(option => {
                          return (
                            <Fragment>
                              <OptionNameSpan
                                isAvailable={
                                  favourite.flatProduct.is_product_available
                                }
                              >
                                {option.name}
                              </OptionNameSpan>{' '}
                              -{' '}
                              {option.sub_options.length
                                ? option.sub_options.map(subOption =>
                                    renderSelectedItemsString(
                                      favourite.flatProduct,
                                      subOption
                                    )
                                  )
                                : renderSelectedItemsString(
                                    favourite.flatProduct,
                                    option
                                  )}
                            </Fragment>
                          );
                        })
                        .map(item => <div>{item}</div>)}
                  </Description>
                )}
                <FavouriteButtonRow justifyContent="space-between !important">
                  <CustomizeButtonWrapper
                    id="FavouritesList-CustomizeButton"
                    isAvailable={favourite.flatProduct.is_product_available}
                    onClick={() =>
                      favourite.flatProduct.is_product_available
                        ? editPreviousProductConfiguration(
                            favourite.flatProduct,
                            true,
                            true,
                            favourite.favouriteId
                          )
                        : null
                    }
                  >
                    <EditIcon />
                    <div>Customize</div>
                  </CustomizeButtonWrapper>
                  {favourite.flatProduct.is_product_available ? (
                    <AddButton
                      id="FavouritesList-AddToCart"
                      onClick={e => {
                        addToCart({
                          ...favourite,
                          isEditing: false,
                          isFromPreviousOrder: false,
                          isFromFavourites: true,
                          requestingGetProductToConfigure: false,
                          selectedVariantId: favourite.flatProduct.variant_id,
                          ...calculatePrice(
                            modifyFavouriteProducts(
                              favourite.flatProduct,
                              products
                            )
                          ),
                        });
                        e.stopPropagation();
                      }}
                    />
                  ) : (
                    <p
                      id="FavouritesList-SoldOut"
                      style={{
                        color: appConfig.styleConfig.unavailableProductColor,
                      }}
                    >
                      Sold Out
                    </p>
                  )}
                </FavouriteButtonRow>
              </InfoSection>
            </Wrapper>
          ))
        )}
      </CardWrapper>

      {/* {isFromFavourites && (
        <ProductCustomizerContainer
          navigateToMenu={() => window.history.pushState(null, '', `/menu/`)}
        />
      )} */}
    </div>
  );
};

export default FavouritesList;
